import React, {useState, useEffect, useRef} from 'react';
import './Match.css';
import {listClubProam, listClubProad, listClubProbd, listClubProbm, URL_WS_URL, URL_WS_PORT, domain} from '../variables';
import Partie from './Partie';
import {
    BsFillCaretRightFill,
    BsRecordCircleFill,
    BsEyeFill
} from "react-icons/bs";
import axios from 'axios';
import LogoClub from './LogoClub';
import AnimateHeight from 'react-animate-height';
import io from 'socket.io-client';
import CartridgePlayers from './CartridgePlayers';

function getWindowSize() {
    const {innerWidth, innerHeight} = window;
    return {innerWidth, innerHeight};
}

// Rapproche un nom de joueur entre getRencDetails.php ("NOM Prenom", casse
// normale) et le livescoring dafunker (parfois "NOM PRENOM" en majuscules,
// avec ou sans accents selon les matchs) : on compare sans tenir compte de la
// casse ni des accents pour ne pas rater un rapprochement valide.
function normalizePlayerName(name) {
    return (name || "")
        .toUpperCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "");
}

// variant "cards" (listes en cartes, cf. .match_cards dans Match.css) : pas de bordure
// de résultat en style inline, vainqueur en gras dès que le score officiel est connu.
// variant "club" (onglet Calendrier / Résultats d'un club) : idem, et le résultat du
// club est porté par une classe (win / loss / pending)
const Match = ({championnat, match, headerLivescore, currentDay, deploy, currentClub = null, showTour = false, variant = 'default'}) => {
    const [socket, setSocket] = useState(null);
    const [clubOne, setClubOne] = useState('');
    const [clubTwo, setClubTwo] = useState('');
    const [showDetails, setShowDetails] = useState(false);
    const [details, setDetails] = useState([]);

    const [livescore, setLivescore] = useState([]);
    const [jsonMatch, setJsonMatch] = useState(null);
    // En-tête websocket (clé "headers" au pluriel) : { heure_debut, num_specs, num_total_specs }
    const [headers, setHeaders] = useState(null);

    const livescoreRef = useRef(livescore);
    const jsonMatchRef = useRef(jsonMatch);
    // Empêche un double-clic (ou un double déclenchement d'event) de lancer
    // deux fetchs en parallèle sur le même match, ce qui pouvait faire
    // écraser les résultats officiels par une réponse vide/en erreur.
    const isFetchingDetailsRef = useRef(false);

    const [liveTeamA, setLiveTeamA] = useState([]);
    const [liveTeamX, setLiveTeamX] = useState([]);
    // Drapeaux du cartouche (mode "SPID", résultats officiels) : non fournis
    // par getRencDetails.php, complétés via le livescoring (cf. refreshHistoriqueForDetails).
    const [liveTeamACountryCodes, setLiveTeamACountryCodes] = useState([]);
    const [liveTeamXCountryCodes, setLiveTeamXCountryCodes] = useState([]);
    // Noms bruts (sans le "N°.. - classement") des joueurs du cartouche, dans
    // l'ordre A/B/C et X/Y/Z de getRencDetails.php. Cet ordre ne correspond pas
    // forcément à l'ordre des slots JA1..JA4/JB1..JB4 côté dafunker (constaté
    // sur un match où X/Y étaient inversés) : on rapproche donc par nom exact
    // plutôt que par position dans refreshHistoriqueForDetails.
    const teamANamesRef = useRef([]);
    const teamXNamesRef = useRef([]);

    useEffect(() => {
        // On initialise nos paramètres
        setClubOne(getClubOne());
        setClubTwo(getClubTwo());
        // si on arrive depuis l'acceuil
        // alors on déploie le match sélectionné
        if (deploy === getRencID()) {
            if (headerLivescore.status === "en_cours") {
                setShowDetails(true);
            }
            else if (hasOfficialScore()) {
                getDetailsMatch();
            }
            else {
                getDetailsDafunker();
            }
        }
    }, []);
    
    useEffect(() => {
        // const today = new Date().toLocaleDateString("fr-FR");

        // on ne veut pas que la connexion socket soit établit si le match est fermé
        if (!showDetails) {
            // si livescoring
            if (headerLivescore.status === 'en_cours' && socket !== null) {
                // on désactive le socket si on n'a pas déroulé le match
                socket.disconnect();
                setSocket(null);
                return;
            }
        }


        // on initialise le socket pour les matchs déroulés d'aujourd'hui 
        if (headerLivescore.status === 'en_cours') {
            const link = URL_WS_URL + ":" + URL_WS_PORT + "/livescore_" + getRencID() + "?" + getCData().replaceAll(',', '&');
            const tmpSocket = io(link);

            // Message de connexion
            tmpSocket.on('connect', () => {
            });
    

            // Ecouter les messages d'update de l'entete du match
            tmpSocket.on('update', (jsonSocket) => {
                parseLivescoreForPartie(jsonSocket);
                jsonMatchRef.current = jsonSocket;
                setJsonMatch(jsonSocket);
                if (jsonSocket.headers) {
                    setHeaders(jsonSocket.headers);
                }
            });

            // Ecouter les messages d'update du match
            tmpSocket.on('update_partie', (jsonPartie) => {
                mergeJsonPartieWithLivescore(jsonPartie);
            });

            // Ecouter les mises à jour de l'en-tête seul (compteur de spectateurs, etc.)
            // le payload de cet event EST l'objet headers (pas d'enveloppe)
            tmpSocket.on('update_headers', (jsonHeaders) => {
                setHeaders(jsonHeaders);
            });

    
            // on sauvegarde le socket
            setSocket(tmpSocket);
    
            // Nettoyage de l'effet lors du démontage du composant
            return () => {
                tmpSocket.disconnect();
            };
        }

    }, [showDetails]);

    // La rencontre est remontée sur les API de la fédération dès que
    // scorea/scoreb sont renseignés (getEquResult.php). Dans ce cas on
    // affiche le score officiel via getRencDetails.php ; sinon on n'interroge
    // QUE le livescoring (dafunker). Dans le premier cas, on interroge quand
    // même en plus le livescoring (cf. enrichDetailsWithHistorique) pour
    // récupérer l'historique détaillé (score_history) de chaque partie et
    // permettre l'affichage de "Historique" même sur un match terminé.
    const hasOfficialScore = () => {
        return match?.scorea !== undefined && match?.scorea !== '' &&
            match?.scoreb !== undefined && match?.scoreb !== '';
    }

    const getDetailsMatch = async () => {
        const link = `${domain}/server/getRencDetails.php?renc_id=` + getRencID();
        try {
            const res = await axios.get(link);
            setDetails(parseJSONDetails(res.data));
            refreshHistoriqueForDetails();
        }
        catch (error) {
            console.log(error);
        }
        isFetchingDetailsRef.current = false;
        setShowDetails(true);
    }

    // Complète les parties officielles (getRencDetails.php, sans score_history)
    // avec l'historique détaillé récupéré depuis le livescoring (dafunker), en
    // faisant correspondre chaque partie par nom des deux joueurs (les deux
    // sources listent les parties dans le même ordre mais dafunker est la
    // seule à exposer un identifiant par partie).
    const refreshHistoriqueForDetails = async () => {
        try {
            const link = URL_WS_URL + "/livescore/get/" + getRencID();
            const res = await axios.get(link, {
                "Content-Type": "application/xml; charset=utf-8"
            });
            const dafunkerMatch = res.data;
            if (!dafunkerMatch?.parties || !dafunkerMatch?.equipe1 || !dafunkerMatch?.equipe2) {
                return;
            }

            const getFullName = (equipe, slotId) => {
                const joueur = equipe?.joueurs?.[slotId];
                return joueur ? normalizePlayerName(joueur.nom + " " + joueur.prenom) : "";
            }

            setDetails(prevDetails => prevDetails.map((partie) => {
                const dafunkerPartie = dafunkerMatch.parties.find((p) =>
                    getFullName(dafunkerMatch.equipe1, p.joueur1.slotId) === normalizePlayerName(partie.ja) &&
                    getFullName(dafunkerMatch.equipe2, p.joueur2.slotId) === normalizePlayerName(partie.jb)
                );
                if (!dafunkerPartie) {
                    return partie;
                }
                return {
                    ...partie,
                    score_history: dafunkerPartie.score_history,
                    premier_serveur: dafunkerPartie.premier_serveur,
                    cards: dafunkerPartie.cards,
                    countryCodeJA: dafunkerMatch.equipe1?.joueurs?.[dafunkerPartie.joueur1.slotId]?.country_code,
                    countryCodeJB: dafunkerMatch.equipe2?.joueurs?.[dafunkerPartie.joueur2.slotId]?.country_code,
                };
            }));

            // Idem pour le cartouche des joueurs (mode "SPID"). L'ordre A/B/C
            // (resp. X/Y/Z) de getRencDetails.php ne correspond pas toujours à
            // l'ordre des slots JA1..JA4 (resp. JB1..JB4) côté dafunker : on
            // rapproche donc chaque joueur par nom (normalisé), tous slots
            // confondus, plutôt que par position.
            const countryCodeByName = {};
            [dafunkerMatch.equipe1, dafunkerMatch.equipe2].forEach((equipe) => {
                Object.values(equipe?.joueurs || {}).forEach((joueur) => {
                    if (!joueur) return;
                    countryCodeByName[normalizePlayerName(joueur.nom + " " + joueur.prenom)] = joueur.country_code;
                });
            });
            setLiveTeamACountryCodes(teamANamesRef.current.map(nom => countryCodeByName[normalizePlayerName(nom)]));
            setLiveTeamXCountryCodes(teamXNamesRef.current.map(nom => countryCodeByName[normalizePlayerName(nom)]));
        }
        catch (error) {
            console.log(error);
        }
    }

    const getDetailsDafunker = async () => {
        const link = URL_WS_URL + "/livescore/get/" + getRencID();
        try {
            const res = await axios.get(link, {
                "Content-Type": "application/xml; charset=utf-8"
            });
            parseLivescoreForPartie(res.data);
        }
        catch (error) {
            console.log(error);
        }
        isFetchingDetailsRef.current = false;
        setShowDetails(true);
    }

    const handleShowDetails = (e, responsive = false) => {
        e.preventDefault();

        // Match pas encore commencé : rien à afficher. Mais si la FFTT a déjà publié
        // le résultat, il prime sur un livescoring resté à "bientot" (ouvert puis jamais
        // utilisé côté dafunker) et on affiche la feuille de match officielle.
        if(headerLivescore.status === "bientot" && !hasOfficialScore())
        {
            return false;
        }

        if (responsive) {
            if (getWindowSize().innerWidth > 1000) {
                return false;
            }
        }

        if (showDetails) {
            setShowDetails(false);
            return;
        }

        if (headerLivescore.status === "en_cours") {
            // rien, l'affichage en direct est géré par le socket livescoring
        }
        else if (details.length === 0 && livescore.length === 0) {
            if (isFetchingDetailsRef.current) {
                return;
            }
            isFetchingDetailsRef.current = true;
            if (hasOfficialScore()) {
                getDetailsMatch();
            }
            else {
                getDetailsDafunker();
            }
            return;
        }

        setShowDetails(!showDetails);
    }

    const parseJSONDetails = (data) => {
        const parties = [];
        const teamA = [];
        const teamX = [];

        (data.parties || []).forEach((partie) => {
            const object = {};
            Object.keys(partie).forEach((key) => {
                // on adapte le score pour correspondre au format livescore
                // 09 -05 07 -09 06 ->>>> [[11, 9], [5, 11], [11, 7], [9, 11], [11, 6]]
                if (key === "detail") {
                    // notre array qui contient le score
                    const sets = [];
                    // on split le string pour récupérer chaque set
                    // (detail vide = partie non jouée, la rencontre étant déjà décidée)
                    const scores = partie.detail.split(' ').filter((score) => score !== '');
                    // on insère chaque set dans un array pour correspondre au bon format
                    scores.forEach((score) => {
                        const scoreBrut = parseInt(score.replace('-', ''));
                        let scoreJA = 0; let scoreJB = 0;

                        // défaite du joueur A
                        if (score.includes('-')) {
                            scoreJA = parseInt(scoreBrut);
                            scoreJB = parseInt(scoreBrut >= 10 ? scoreBrut + 2 : 11);
                        }
                        // victoire du joueur A
                        else {
                            scoreJA = parseInt(scoreBrut >= 10 ? scoreBrut + 2 : 11);
                            scoreJB = parseInt(scoreBrut);
                        }

                        // à noter que le parseInt retire le 0 devant le score
                        // utile pour récupérer le score directement

                        // on insère les scores dans notre array
                        sets.push([scoreJA, scoreJB]);
                    });
                    object.detail = sets;
                }
                else {
                    object[key] = partie[key];
                }
            })
            // on insère la partie
            if(Object.keys(object).length > 0)
            {
                parties.push(object);
            }
        });

        const teamANames = [];
        const teamXNames = [];

        (data.joueurs || []).forEach((joueur) => {
            if(joueur[0]?.length > 0 && joueur[1]?.length > 0)
            {
                teamA.push(joueur[0] + " " + joueur[1]);
                teamANames.push(joueur[0]);
            }
            if(joueur[2]?.length > 0 && joueur[3]?.length > 0)
            {
                teamX.push(joueur[2] + " " + joueur[3]);
                teamXNames.push(joueur[2]);
            }
        })
        if(teamA.length > 0)
        {
            setLiveTeamA(teamA);
            teamANamesRef.current = teamANames;
        }
        if(teamX.length > 0)
        {
            setLiveTeamX(teamX);
            teamXNamesRef.current = teamXNames;
        }
        return parties;
    }

    const CalSetA = (score) =>  {
        let total = 0;
        score.forEach((game) => {
           
            if(game[0] > game[1])
            {
                total = total + 1;
            }
        })
        return total;
    }

    const CalSetX = (score) =>  {
        let total = 0;
        score.forEach((game) => {
           
            if(game[0] < game[1])
            {
                total = total + 1;
            }
        })
        return total;
    }

    const parseLivescoreForPartie = (match) => {
        const parties = [];
        match.parties.forEach((partie) => {

             // on récupère le slotID des joueurs
            const slotIdJA = partie.joueur1.slotId;
            const slotIdJB = partie.joueur2.slotId;
            let fullNameJA = ""; let fullNameJB = "";

            if (slotIdJA !== "") {
                fullNameJA = match.equipe1.joueurs[slotIdJA].nom + " " + match.equipe1.joueurs[slotIdJA].prenom;
            }

            if (slotIdJB !== "") {
                fullNameJB = match.equipe2.joueurs[slotIdJB].nom + " " + match.equipe2.joueurs[slotIdJB].prenom;
            }
 
            parties.push({
                "id": partie.id,
                "ja": fullNameJA,
                "jb": fullNameJB,
                "scorea": '' + CalSetA(partie.score),
                "scoreb": '' + CalSetX(partie.score),
                "teamA" : match.equipe1?.score,
                "teamX" : match.equipe2?.score,
                "detail": partie.score,
                "premier_serveur": partie.premier_serveur,
                "score_history": partie.score_history,
                "cards": partie.cards,
                "statut": partie.status,
                "countryCodeJA": match.equipe1?.joueurs?.[slotIdJA]?.country_code,
                "countryCodeJB": match.equipe2?.joueurs?.[slotIdJB]?.country_code,
            });
        })
        livescoreRef.current = parties;
        setLivescore(parties);

        const joueursA = [];
        joueursA.push(match?.equipe1?.joueurs?.JA1);
        joueursA.push(match?.equipe1?.joueurs?.JA2);
        joueursA.push(match?.equipe1?.joueurs?.JA3);
        joueursA.push(match?.equipe1?.joueurs?.JA4);
        setLiveTeamA(joueursA);

        const joueursX = [];
        joueursX.push(match?.equipe2.joueurs?.JB1);
        joueursX.push(match?.equipe2.joueurs?.JB2);
        joueursX.push(match?.equipe2.joueurs?.JB3);
        joueursX.push(match?.equipe2.joueurs?.JB4);
        setLiveTeamX(joueursX);
    }

    const mergeJsonPartieWithLivescore = (partieToMerge) => {
        let livescorePartie = livescoreRef.current.findIndex(partie => partie.id === partieToMerge.id);
        let tmpLivescore = [...livescoreRef.current];
        // on regarde si le match a été supprimé
        if (partieToMerge.status === "deleted") {
            // si on avait le match alors on supprime
            if (livescorePartie !== -1) {
                tmpLivescore = tmpLivescore.filter(partie => partie.id !== partieToMerge.id);
                livescoreRef.current = tmpLivescore;
                setLivescore(tmpLivescore)
            }
            return;
        }

        // on récupère le slotID des joueurs
        const slotIdJA = partieToMerge.joueur1.slotId;
        const slotIdJB = partieToMerge.joueur2.slotId;
        let fullNameJA = ""; let fullNameJB = "";

        if (slotIdJA !== "") {
            fullNameJA = jsonMatchRef.current.equipe1.joueurs[slotIdJA].nom + " " + jsonMatchRef.current.equipe1.joueurs[slotIdJA].prenom;
        }

        if (slotIdJB !== "") {
            fullNameJB = jsonMatchRef.current.equipe2.joueurs[slotIdJB].nom + " " + jsonMatchRef.current.equipe2.joueurs[slotIdJB].prenom;
        }
        
        const data = {
            "id": partieToMerge.id,
            "ja": fullNameJA,
            "jb": fullNameJB,
            "scorea": '' + CalSetA(partieToMerge.score),
            "scoreb": '' + CalSetX(partieToMerge.score),
            "teamA" : '0',
            "teamX" : '0',
            "detail": partieToMerge.score,
            "premier_serveur": partieToMerge.premier_serveur,
            "score_history": partieToMerge.score_history,
            "cards": partieToMerge.cards,
            "statut": partieToMerge.status,
            "countryCodeJA": jsonMatchRef.current.equipe1?.joueurs?.[slotIdJA]?.country_code,
            "countryCodeJB": jsonMatchRef.current.equipe2?.joueurs?.[slotIdJB]?.country_code,
        };

        // on update/insert
        if (livescorePartie !== -1) {
            tmpLivescore[livescorePartie] = data
        }
        else {
            tmpLivescore.push(data);
        }
        

        // on met à jour les matchs
        livescoreRef.current = tmpLivescore;
        setLivescore(tmpLivescore)
    }

    const getCData = () => {
        if (match === null) return null;
        return match.lien;
    }

    const getRencID = () => {
        const cData = match.lien;
        const rencID = cData.split('renc_id=')[1].split('&')[0];
        return rencID;
    }

    const getCorrectListClub = (value) => {
        switch (value) {
            case 'proam':
                return listClubProam;
            case 'probm':
                return listClubProbm;
            case 'proad':
                return listClubProad;
            case 'probd':
                return listClubProbd;
            default:
                break;
        }
    }

    const getClubOne = () => {
        const team = match.equa;
        const cData = match.lien;
        const num = cData.split('clubnum_1=')[1].split('&')[0];
        return getCorrectListClub(championnat).find(item => item.identifier.trim() === num)  || getCorrectListClub(championnat).find(item => item.title === team);
    }

    const getClubTwo = () => {
        const team = match.equb;
        const cData = match.lien;
        const num = cData.split('clubnum_2=')[1].split(' ')[0];
        return getCorrectListClub(championnat).find(item => item.identifier.trim() === num) || getCorrectListClub(championnat).find(item => item.title === team);
    }

    const getDateMatch = () => {
        const value = match.datereelle
        const date = new Date(value.split('/')[2] + '-' + value.split('/')[1] + '-' + value.split('/')[0]);
        const day = date.getDate();
        let month = date.toLocaleString('default', { month: 'long' });

        if (month.length > 3) {
            month = month.substring(0, 3) + '.';
        }

        return day + " " + month;
    }

    const getTimeMatch = () => {
        return match.heurereelle || '';
    }


    const getScoreA = () => {
        if (headerLivescore.status === 'en_cours') {
            if (livescore.length > 0) {
                return parseInt(livescore[0].teamA);
            }
            else {
                return parseInt(headerLivescore.score.split('-')[0]);
            }
        }
        else if (headerLivescore.status === "termine") {
            return parseInt(headerLivescore.score.split('-')[0]);
        }
        else {
            return parseInt(match.scorea);
        }
    }

    const getScoreX = () => {
        if (headerLivescore.status === 'en_cours') {
            if (livescore.length > 0) {
                return parseInt(livescore[0].teamX);
            }
            else {
                return parseInt(headerLivescore.score.split('-')[1]);
            }
        }
        else if (headerLivescore.status === "termine") {
            return parseInt(headerLivescore.score.split('-')[1]);
        }
        else {
            return parseInt(match.scoreb);
        }
    }

    const getIdentifierClub = (index) => {
        if (match) {
            const cData = match.lien.trim();
            if (index === 1) {
                return cData.split('clubnum_1=')[1].split('&')[0];
            }
            if (index === 2) {
                return cData.split('clubnum_2=')[1].split(' ')[0];
            }
    
        }
        return null;
    }

    const currentClubIsWinner = () => {
        const equa = match.equa.trim();
        const equb = match.equb.trim();
        const identifier = currentClub.identifier
        if (equa === currentClub.title.trim() || identifier === getIdentifierClub(1)) {
            return parseInt(getScoreA()) > parseInt(getScoreX());
        }
        else if (equb === currentClub.title.trim() || identifier === getIdentifierClub(2)) {
            return parseInt(getScoreX()) > parseInt(getScoreA());
        }
        return false;
    }

    const getStatus = () => {
        const today = new Date().toLocaleDateString("fr-FR");

        switch (headerLivescore.status) {
            case "bientot":
                if (match.datereelle === today) {
                    return "bientot";
                }
                return "";
            default:
                return headerLivescore.status;
        }
    }

    const getStatusMatch = () => {
        return (getStatus() === "termine" && match?.winner) ? "termine_live" : getStatus();
    }

    // true si on doit afficher les données du livescoring (dafunker) plutôt
    // que les données officielles FFTT (cf. hasOfficialScore()).
    const showLivescore = () => {
        return headerLivescore.status === "en_cours" || !hasOfficialScore();
    }

    const getTotalPartie = () => {
        return showLivescore() ? livescore.length : details.length;
    }

    // Match terminé : via le livescoring, ou (hors affichage par défaut) via le score officiel
    const isFinished = () => {
        if (headerLivescore.status === "termine") return true;
        return variant !== 'default' && headerLivescore.status !== "en_cours" && hasOfficialScore();
    }

    const getResultClass = () => {
        if (variant !== 'club' || currentClub === null || headerLivescore.status === "en_cours") return '';
        if (!(getScoreA() >= 0 && getScoreX() >= 0)) return ' pending';
        return currentClubIsWinner() ? ' win' : ' loss';
    }

    const getColorMatch = () => {
        if (variant === 'club') return {};
        if (currentClub !== null && headerLivescore.status !== "en_cours") {
            let color = "rgb(180, 180, 180)";
            if (getScoreA() >= 0 && getScoreX() >= 0) {
                color = currentClubIsWinner() ? 'green' : 'red'
            }
            return {borderRight: `12px solid ${color}`, borderRightStyle: 'inset'}
        }
        return {};
    }

    return ( 
        <div className="match_all_component">
            <div 
                className={"match_component " + getStatusMatch() + getResultClass()}
                style={getColorMatch()}
                onClick={(e)=>handleShowDetails(e)} >
                <div className="match_informations">

                    {
                        currentDay > 0 &&
                        <span className="match_current_day">J{currentDay}</span>
                    }

                    {/* Left center side */}
                    <div className="match_left_side">
                        <span className="match_team_name" style={{fontWeight: (isFinished() && getScoreA() > getScoreX()) ? 'bold' : 'normal'}}>{clubOne !== undefined && clubOne.title}</span>
                        {
                            (clubOne !== undefined && clubOne.logo !== '') &&
                            <LogoClub club={clubOne} width='64' height='64' activateLink={false} />
                        }
                    </div>
                    {/* Afficha du score ou de la date selon l'état du match */}
                    <div className={`informations_center ${(getScoreA() >= 0 && getScoreX() >= 0) ? "scores" : "" }`}>
                        {
                            ((getScoreA() >= 0 && getScoreX() >= 0) && (
                                <>
                                    <span className="score_container" style={{minWidth: 30, whiteSpace: 'nowrap'}}>
                                        <span className="match_score top" style={{fontWeight: (isFinished() && getScoreA() > getScoreX()) ? 'bold' : 'normal'}}>{getScoreA()}</span>
                                        <span className="match_score middle"> - </span>
                                        <span className="match_score bottom" style={{fontWeight: (isFinished() && getScoreA() < getScoreX()) ? 'bold' : 'normal'}}>{getScoreX()}</span>
                                    </span>
                                    {(headerLivescore.status === "en_cours" ) && (
                                        <div className='playoffdown_date'>
                                            En&nbsp;cours
                                        </div>
                                    )}
                                </>
                            ))
                            || (headerLivescore.status !== "en_cours" && (
                                <span className="date_container" style={{display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 75, maxWidth: 75}}>
                                    <span className="match_date"> {getDateMatch()}</span>
                                    <span className="match_time">{getTimeMatch()}</span>
                                    {showTour && headerLivescore.tour && <span className="match_tour">{"journée n°" + (headerLivescore.tour || "")}</span>}
                                </span>
                            ))
                        }
                    </div>
                    

                    {/* Right center side */}
                    <div className="match_right_side">
                        {
                            (clubTwo !== undefined && clubTwo.logo !== '') &&
                            <LogoClub club={clubTwo} width='64' height='64' activateLink={false} />
                        }
                        <span className="match_team_name" style={{fontWeight: (isFinished() && getScoreA() < getScoreX()) ? 'bold' : 'normal'}}>{clubTwo !== undefined && clubTwo.title}</span>
                    </div>
                </div>
                
                {/* On affiche la flèche de défilement pour obtenir la feuille de match officiel
                si le match est terminé */}
                {/* Le clic est déjà géré par le onClick du match_component parent :
                pas de onClick ici pour éviter un double déclenchement (bubbling). */}
                {
                    headerLivescore.status !== "en_cours" && getScoreA() >= 0 && getScoreX() >= 0
                    &&
                    <BsFillCaretRightFill className={showDetails ? "match_arrow active" : "match_arrow"} size={38} />
                }

                {/* Si le match est en train d'être livescorer alors on affiche le record */}
                {
                    (getStatus() === "en_cours") && getWindowSize().innerWidth > 1000 &&
                    <BsFillCaretRightFill color='red' className={showDetails ? "match_arrow active" : "match_arrow"} size={38} />
                }
                {
                    (getStatus() === "bientot") && getWindowSize().innerWidth > 1000 &&
                    <span  
                        style={{
                            backgroundColor: "var(--yellow-color)",
                            top: "50%",
                            borderRadius: 5,
                            padding: "5px 10px",
                            transform: "translateY(-50%)",  
                            right: 10, 
                            position: 'absolute',
                            fontSize: 18,
                            transition: '0.2s ease-in-out all',
                        }}
                    >Aujourd'hui</span>
                    }
            </div>
            {
                <AnimateHeight 
                    key={getRencID()} 
                    id={getRencID()}
                    duration={500}
                    height={showDetails ? 'auto' : 0}
                >
                    {
                        (getTotalPartie() > 0 || headerLivescore.status === "en_cours") &&
                        <div className="details_component">
                            {
                                headerLivescore.status === "en_cours" &&
                                <div className="details_live_bar">
                                    <span className="details_live">
                                        <BsRecordCircleFill /> LIVE
                                    </span>
                                    <span className="details_specs" title="Spectateurs">
                                        <BsEyeFill /> {headers?.num_specs ?? 0}
                                    </span>
                                </div>
                            }
                            {
                                getTotalPartie() > 0 &&
                                <h2 className="details_count">
                                    {getTotalPartie()} {getTotalPartie() > 1 ? 'parties disputées' : 'partie disputée'}
                                </h2>
                            }
                            {/* Affichage du cartouche des joueurs */}
                            {(showLivescore() &&
                                <CartridgePlayers id={getRencID()} liveTeamA={liveTeamA} liveTeamX={liveTeamX} isFromSPID={false}></CartridgePlayers>)
                            ||
                                <CartridgePlayers id={getRencID()} liveTeamA={liveTeamA} liveTeamX={liveTeamX} countryCodesA={liveTeamACountryCodes} countryCodesX={liveTeamXCountryCodes} isFromSPID={true}></CartridgePlayers>
                            }
                            {/* Affichage du détail des matchs */}
                            <div className="details_parties">
                                {
                                        (showLivescore() &&
                                        livescore?.map((detail, index) => {
                                            return (
                                                <div
                                                    key={index}
                                                    style={{marginBottom: 20}}>
                                                    <Partie status={headerLivescore.status} match={detail} teamJA={getClubOne()} teamJB={getClubTwo()} isLivescoring onRefreshHistorique={getDetailsDafunker} />
                                                </div>
                                            )
                                        }))
                                        ||
                                        details?.map((detail, index) => {
                                            return (
                                                <div
                                                    key={index}
                                                    style={{marginBottom: 20}}>
                                                    <Partie status={headerLivescore.status} match={detail} teamJA={getClubOne()} teamJB={getClubTwo()} isOffDown onRefreshHistorique={refreshHistoriqueForDetails} />
                                                </div>
                                            )
                                        })
                                }
                            </div>
                        </div>
                    }
                </AnimateHeight>
            }
            
        </div>
        
    );
}

export default Match;